/*
 * @Author: Yun 
 * @Date: 2025-11-21 15:53:47 
 * @Last Modified by: Yun
 * @Last Modified time: 2025-11-26 18:17:33
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { content } from '@configs/content';
import Modal from './Modal';
import SmartReader, { NewsContent } from '@components/SmartReader';
import styles from './index.module.scss';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface HighLightItem {
  id: number;
  image: string;
  caption: string;
  newsContent?: NewsContent;
}

const HighLight: React.FC = () => {
  const { title, subtitle, items } = content.highLight;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [selectedItem, setSelectedItem] = useState<HighLightItem | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  // 预计算粒子样式，避免重新渲染时重复计算随机值
  const particleStyles = useMemo(() => 
    Array.from({ length: 10 }, () => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${8 + Math.random() * 8}s`
    }))
  , []);

  // 视口检测：当组件离开视口时暂停所有CSS动画
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsPaused(!entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  // 计算每个豆腐块的类名
  const getItemClassName = (index: number) => {
    if (hoveredIndex === null) return styles.item;
    if (index === hoveredIndex) return `${styles.item} ${styles.active}`;
    
    const distance = Math.abs(index - hoveredIndex);
    if (distance === 1) return `${styles.item} ${styles.far1}`;
    if (distance === 2) return `${styles.item} ${styles.far2}`;
    if (distance === 3) return `${styles.item} ${styles.far3}`;
    if (distance === 4) return `${styles.item} ${styles.far4}`;
    
    return styles.item;
  };

  // 防抖标志和点击位置
  const [isOpening, setIsOpening] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);

  // 处理豆腐块点击（带防抖）
  const handleItemClick = (item: HighLightItem, event: React.MouseEvent<HTMLDivElement>) => {
    // 阻止事件冒泡
    event.stopPropagation();
    event.preventDefault();
    
    // 防止快速重复点击
    if (isOpening) return;
    
    if (item.newsContent) {
      // 获取点击元素的中心位置
      const rect = event.currentTarget.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      
      setClickPosition({ x, y });
      setIsOpening(true);
      setSelectedItem(item);
      // 500ms 后重置防抖标志
      setTimeout(() => setIsOpening(false), 500);
    } else {
      console.warn(`No news content found for item ${item.id}`);
    }
  };

  // 关闭弹窗
  const handleCloseModal = () => {
    setSelectedItem(null);
    setClickPosition(null);
  };

  // 入场动画
  useGSAP(() => {
    if (!containerRef.current) return;

    // 创建时间线 - 每次进入视窗都重置
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top 80%',
        end: 'bottom 20%',
        onEnter: () => tl.restart(),
        onEnterBack: () => tl.restart(),
      }
    });

    // 标题动画
    tl.fromTo(
      containerRef.current.querySelector(`.${styles.title}`),
      { opacity: 0, y: -30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      },
      0
    );

    // 分割线动画
    tl.fromTo(
      containerRef.current.querySelector(`.${styles.divider}`),
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: 0.8,
        ease: 'power3.out'
      },
      0.2
    );

    // 副标题动画
    tl.fromTo(
      containerRef.current.querySelector(`.${styles.subtitle}`),
      { opacity: 0, y: -20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      },
      0.3
    );

    // 标题动画
    tl.fromTo(
      `.${styles.title}`,
      { opacity: 0, y: -30 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      },
      0
    );

    // 副标题动画
    tl.fromTo(
      `.${styles.subtitle}`,
      { opacity: 0, y: -20 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out'
      },
      0.2
    );

    // 豆腐块波纹扩散动画 - 从中间向两侧，带旋转和弹性效果
    const animationOrder = [2, 1, 3, 0, 4]; // 中间->左右->最外侧
    
    animationOrder.forEach((index, orderIndex) => {
      if (itemsRef.current[index]) {
        tl.fromTo(
          itemsRef.current[index],
          { 
            opacity: 0, 
            scale: 0.7,
            y: 50,
            rotationY: -15,
            rotationX: 10
          },
          {
            opacity: 1,
            scale: 1,
            y: 0,
            rotationY: 0,
            rotationX: 0,
            duration: 0.8,
            ease: 'back.out(1.4)'
          },
          0.5 + orderIndex * 0.12
        );
      }
    });

    // 丝带卷轴展开动画 - 从顶部往下依次释放
    const ribbons = containerRef.current.querySelectorAll(`.${styles.ribbon}`);
    if (ribbons.length > 0) {
      gsap.set(ribbons, { transformOrigin: 'top center' });
      tl.fromTo(
        ribbons,
        { scaleY: 0, opacity: 0 },
        {
          scaleY: 1,
          opacity: 1,
          duration: 1.2,
          stagger: 0.15,
          ease: 'power3.out'
        },
        0
      );
    }

    // 粒子动画
    const particles = containerRef.current.querySelectorAll(`.${styles.particle}`);
    if (particles.length > 0) {
      tl.fromTo(
        particles,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          stagger: 0.05,
          ease: 'power1.out'
        },
        0.3
      );
    }
  }, { scope: containerRef });

  return (
    <section ref={containerRef} className={`${styles.highLightSection}${isPaused ? ` ${styles.paused}` : ''}`} id="highlight">
      {/* 渐变背景 */}
      <div className={styles.background} />
      
      {/* 漂浮丝带背景 */}
      <div className={styles.ribbonsBackground}>
        <div className={styles.ribbonsWrapper}>
          {[...Array(6)].map((_, i) => (
            <div 
              key={i} 
              className={styles.ribbon}
              style={{
                animationDelay: `${i * -1.5}s`,
                animationDuration: `${12 + i * 2}s`
              }}
            />
          ))}
        </div>
      </div>
      
      {/* 漂浮粒子 */}
      <div className={styles.particlesBackground}>
        {particleStyles.map((style, i) => (
          <div 
            key={i} 
            className={styles.particle}
            style={style}
          />
        ))}
      </div>
      
      <div className={styles.header}>
        <h2 className={styles.title}>{title}</h2>
        {/* <div className={styles.divider} /> */}
        <p className={styles.subtitle}>{subtitle}</p>
      </div>

      <div 
        className={styles.itemsContainer}
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => {
              itemsRef.current[index] = el;
            }}
            className={getItemClassName(index)}
            onMouseEnter={() => setHoveredIndex(index)}
            onClick={(e) => handleItemClick(item as HighLightItem, e)}
          >
            <div className={styles.imageWrapper}>
              <img src={item.image} alt={item.caption} className={styles.image} />
            </div>
            <span className={styles.caption}>{item.caption}</span>
          </div>
        ))}
      </div>

      {/* 新闻弹窗 */}
      <Modal isOpen={!!selectedItem} onClose={handleCloseModal} originPosition={clickPosition}>
        {selectedItem?.newsContent && (
          <SmartReader isModal={true} content={selectedItem.newsContent} />
        )}
      </Modal>
    </section>
  );
};

export default HighLight;
